/* SCENE v1 — product page behaviour. Plain ES module, no dependencies.
 * Cart integration reuses the theme's own events (@shopify/events) so the cart drawer and cart count update as usual. */

const eventsPromise = import('@shopify/events').catch(() => null);

const routes = () => (window.Theme && window.Theme.routes) || {};

/**
 * Adds one line to the cart and notifies the theme (cart drawer, cart bubble).
 * @param {FormData} fd
 * @param {HTMLElement} el   element used to dispatch the cart events from
 * @param {string} productId
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
async function addToCart(fd, el, productId) {
  const events = await eventsPromise;
  const cartUrl = routes().cart_url || '/cart';
  const addUrl = routes().cart_add_url || '/cart/add.js';

  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component')].map((n) => n.dataset.sectionId).filter(Boolean)
    ),
  ];
  if (sectionIds.length) fd.set('sections', sectionIds.join(','));

  const qty = Number(fd.get('quantity')) || 1;
  const deferred = events ? events.CartLinesUpdateEvent.createPromise() : null;

  if (events && deferred) {
    el.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(fd.get('id')), quantity: qty }],
        promise: deferred.promise,
      })
    );
  }

  const refreshCart = async () => {
    const r = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!r.ok) throw new Error('Cart fetch failed');
    return r.json();
  };

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      body: fd,
    });
    const data = await res.json();

    if (data.status) {
      if (events && deferred) {
        el.dispatchEvent(
          new events.CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        refreshCart()
          .then((cart) =>
            deferred.resolve({
              cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: true, items: cart.items, source: 'scene', sourceId: el.id || '', itemCount: qty, productId },
            })
          )
          .catch(deferred.reject);
      }
      return { ok: false, message: data.description || data.message || 'Could not add this item.' };
    }

    if (events && deferred) {
      const cart = await refreshCart();
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'scene',
          sourceId: el.id || '',
          itemCount: qty,
          productId,
          sections: data.sections,
        },
      });
    } else {
      window.location.href = cartUrl;
    }
    return { ok: true };
  } catch (error) {
    console.error(error);
    if (deferred) deferred.reject(error);
    return { ok: false, message: 'Something went wrong. Please try again.' };
  }
}

/* ---------------------------------------------------------------- Product */
class SceneProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this.form = this.querySelector('form[data-scene-form]');
    if (!this.form) return;
    this._ready = true;

    const json = this.querySelector('script[data-scene-variants]');
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.idInput = this.form.querySelector('input[name="id"]');
    this.button = this.form.querySelector('[data-scene-submit]');
    this.priceEl = this.querySelector('[data-scene-price]');
    this.errorEl = this.querySelector('[data-scene-error]');
    this.accel = this.querySelector('[data-scene-accel]');
    this.qty = this.form.querySelector('[data-qty-input]');

    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.querySelectorAll('[data-option-index] input').forEach((i) => i.addEventListener('change', () => this.onOption()));
    this.querySelectorAll('[data-thumb]').forEach((t) =>
      t.addEventListener('click', () => this.showMedia(t.dataset.mediaId))
    );

    const minus = this.querySelector('[data-qty-minus]');
    const plus = this.querySelector('[data-qty-plus]');
    minus && minus.addEventListener('click', () => this.setQty(this.getQty() - 1));
    plus && plus.addEventListener('click', () => this.setQty(this.getQty() + 1));
    this.qty && this.qty.addEventListener('change', () => this.setQty(this.getQty()));
  }

  getQty() {
    return Math.max(1, parseInt((this.qty && this.qty.value) || '1', 10) || 1);
  }
  setQty(n) {
    if (this.qty) this.qty.value = String(Math.max(1, n));
  }

  currentOptions() {
    return [...this.querySelectorAll('[data-option-index]')].map((group) => {
      const checked = group.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  onOption() {
    const opts = this.currentOptions();
    const match = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    this.applyVariant(match || null);
  }

  applyVariant(v) {
    this.clearError();
    if (!v) {
      this.setButton(false, this.button.dataset.labelUnavailable);
      if (this.accel) this.accel.hidden = true;
      return;
    }
    if (this.idInput) {
      this.idInput.value = v.id;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.priceEl) this.priceEl.innerHTML = v.price_html;
    this.setButton(v.available, v.available ? this.button.dataset.labelAdd : this.button.dataset.labelSoldout);
    if (this.accel) this.accel.hidden = !v.available;
    if (v.media_id) this.showMedia(String(v.media_id));
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    } catch (e) {
      /* ignore */
    }
  }

  setButton(enabled, label) {
    this.button.disabled = !enabled;
    const t = this.button.querySelector('[data-scene-label]');
    if (t && label) t.textContent = label;
  }

  showMedia(id) {
    const slides = this.querySelectorAll('[data-slide]');
    if (![...slides].some((s) => s.dataset.mediaId === String(id))) return;
    slides.forEach((s) => (s.hidden = s.dataset.mediaId !== String(id)));
    this.querySelectorAll('[data-thumb]').forEach((t) =>
      t.setAttribute('aria-current', t.dataset.mediaId === String(id) ? 'true' : 'false')
    );
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }
  clearError() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.button.disabled || this.busy) return;
    this.busy = true;
    this.clearError();

    const label = this.button.querySelector('[data-scene-label]');
    const original = label ? label.textContent : '';
    this.button.setAttribute('aria-busy', 'true');
    if (label && this.button.dataset.labelAdding) label.textContent = this.button.dataset.labelAdding;

    const fd = new FormData(this.form);
    [...fd.keys()].filter((k) => k.startsWith('scene-option-')).forEach((k) => fd.delete(k));

    const result = await addToCart(fd, this, this.dataset.productId || '');

    this.button.removeAttribute('aria-busy');
    if (result.ok) {
      if (label && this.button.dataset.labelAdded) label.textContent = this.button.dataset.labelAdded;
      setTimeout(() => {
        if (label) label.textContent = original;
      }, 1600);
    } else {
      if (label) label.textContent = original;
      this.showError(result.message);
    }
    this.busy = false;
  }
}
if (!customElements.get('scene-product')) customElements.define('scene-product', SceneProduct);

/* ------------------------------------------------ Pairs well with (recs) */
class SceneRecs extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.addEventListener('click', async (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-scene-add]') : null;
      if (!btn || btn.disabled) return;
      const label = btn.querySelector('[data-scene-label]') || btn;
      const original = label.textContent;
      btn.disabled = true;
      const fd = new FormData();
      fd.set('id', btn.dataset.variantId);
      fd.set('quantity', '1');
      const result = await addToCart(fd, btn, btn.dataset.productId || '');
      label.textContent = result.ok ? btn.dataset.labelAdded || original : original;
      setTimeout(() => {
        label.textContent = original;
        btn.disabled = false;
      }, 1400);
    });

    const url = this.dataset.url;
    if (!url) return;
    const load = async () => {
      try {
        const res = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const next = doc.querySelector('[data-recs-grid]');
        const grid = this.querySelector('[data-recs-grid]');
        if (next && grid && next.children.length) grid.innerHTML = next.innerHTML;
      } catch (err) {
        /* keep the server-rendered fallback */
      }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((en) => en.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(this);
    } else {
      load();
    }
  }
}
if (!customElements.get('scene-recs')) customElements.define('scene-recs', SceneRecs);

/* ------------------------------------- What's inside: the three acts */
class SceneNotes extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.root = this.closest('.scene-notes') || this;
    this.range = this.querySelector('[data-scrub]');
    this.candle = this.querySelector('[data-candle]');
    this.acts = [...this.querySelectorAll('[data-act]')];
    if (!this.range || !this.acts.length) return;

    this.root.classList.add('is-js');
    this.range.addEventListener('input', () => {
      this.stopAuto();
      this.render(Number(this.range.value));
    });
    this.range.addEventListener('pointerdown', () => this.stopAuto());
    this.range.addEventListener('keydown', () => this.stopAuto());
    this.acts.forEach((act, i) => {
      const go = () => {
        this.stopAuto();
        this.animateTo([8, 50, 94][i] ?? 0, 450);
      };
      act.addEventListener('click', go);
      act.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      });
    });

    this.render(0);

    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((en) => en.isIntersecting)) {
            io.disconnect();
            this.autoplay();
          }
        },
        { threshold: 0.45 }
      );
      io.observe(this);
    }
  }

  render(value) {
    const p = Math.min(100, Math.max(0, value));
    this.range.value = String(p);
    this.range.style.setProperty('--p', `${p}%`);
    if (this.candle) this.candle.style.setProperty('--p', String(p / 100));
    const active = p < 34 ? 0 : p < 67 ? 1 : 2;
    this.acts.forEach((a, i) => a.classList.toggle('is-active', i === active));
  }

  animateTo(target, duration, done) {
    const start = Number(this.range.value);
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / duration);
      const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      this.render(start + (target - start) * eased);
      if (k < 1 && !this._stopped) this._raf = requestAnimationFrame(step);
      else if (done && !this._stopped) done();
    };
    this._stopped = false;
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(step);
  }

  autoplay() {
    this._auto = true;
    this.animateTo(100, 9000, () => {
      this._auto = false;
      this.animateTo(0, 1400);
    });
  }

  stopAuto() {
    this._stopped = true;
    cancelAnimationFrame(this._raf);
  }
}
if (!customElements.get('scene-notes')) customElements.define('scene-notes', SceneNotes);
